import React, {
  ComponentProps,
  Dispatch,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'

import { Observable } from '@apollo/client'
import type { DropdownItem } from '@factorialco/f0-react/dist/experimental'
import { PageHeader, ResourceHeader } from '@factorialco/f0-react/dist/experimental'

import { DateNavigation, ResolvableMeta } from 'modules/core/lib/navigation/routes'
import { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'
import { Meta } from 'modules/core/lib/navigation/types'

import { useMetaContext } from './matches'

export type ResolvedMeta = {
  pathname: string
  data: Meta
}

export type RouteMetaWithPathname = {
  pathname: string
  meta: ResolvableMeta<Meta>
  resource: boolean
  filters?: {
    date?: DateNavigation
  }
}

type PageActionWithBadge = NonNullable<ComponentProps<typeof PageHeader>['actions']>[number] & {
  badge?: number
}

/**
 * Dropdown item for module header menus. Extends f0 DropdownItem with optional
 * disabled (for items not yet implemented). f0 Dropdown passes through to Radix.
 */
export type ModuleHeaderDropdownItem = DropdownItem & { disabled?: boolean }

/**
 * PageHeader action shape - used by modules to add buttons to the main header.
 * Order within the array is preserved: first action renders leftmost.
 * Note: f0-react PageHeader renders productUpdates (discover) before actions,
 * so module actions always appear after discover. Order between module actions
 * and settings (ellipsis) is controlled here: module actions first, then settings.
 * Dropdown actions may use ModuleHeaderDropdownItem[] (labels, separators, disabled).
 */
export type ModuleHeaderAction =
  | PageActionWithBadge
  | (Omit<PageActionWithBadge, 'actions'> & {
      actions: ModuleHeaderDropdownItem[]
    })
export type ModuleHeaderActions = ModuleHeaderAction[]

interface ResponsivePageContext {
  routeMeta: RouteMetaWithPathname | null
  setActionHandler: (actionHandler: (key: string) => void) => void
  headerOverride: Partial<ResourceHeadersPropsWithActions> | null
  setHeaderOverride: Dispatch<SetStateAction<Partial<ResourceHeadersPropsWithActions> | null>>
  moduleHeaderActions: ModuleHeaderActions | null
  setModuleHeaderActions: Dispatch<SetStateAction<ModuleHeaderActions | null>>
  updateMetadataProperty: <
    Value extends Extract<
      NonNullable<NonNullable<ComponentProps<typeof ResourceHeader>['metadata']>[number]>,
      object
    >['value'],
  >(
    label: string,
    value: Value
  ) => void
}

const ResponsivePageContext = React.createContext<ResponsivePageContext | undefined>(undefined)
export const ResponsivePageProvider = ResponsivePageContext.Provider

export const useResponsivePage = () => {
  const context = useContext(ResponsivePageContext)

  if (context === undefined) {
    throw 'useResponsivePage must be used within a ResponsivePageContext'
  }

  return context
}

/**
 * Hook to get and update resource header props dynamically.
 * This allows page components to update the header title, description, actions, metadata,
 * and all other props in real-time when data changes, even if the route meta hasn't updated yet.
 *
 * The `updateHeader` function supports partial updates - you can update just the properties
 * that changed, and they will be merged with the existing route meta header props.
 *
 * @example
 * ```tsx
 * const { headerProps, updateHeader } = useResourceHeader()
 *
 * // Update just the title when it changes
 * useEffect(() => {
 *   if (data?.resource) {
 *     updateHeader({
 *       title: data.resource.title,
 *     })
 *   }
 * }, [data?.resource?.title, updateHeader])
 *
 * // Update description separately
 * useEffect(() => {
 *   if (data?.resource) {
 *     updateHeader({
 *       description: data.resource.description,
 *     })
 *   }
 * }, [data?.resource?.description, updateHeader])
 *
 * // Update actions when permissions change
 * useEffect(() => {
 *   if (data?.resource) {
 *     updateHeader({
 *       primaryAction: canEdit ? { label: 'Edit', key: 'edit' } : undefined,
 *       otherActions: canDelete ? [{ label: 'Delete', key: 'delete', critical: true }] : undefined,
 *     })
 *   }
 * }, [canEdit, canDelete, updateHeader])
 *
 * // Update metadata
 * useEffect(() => {
 *   if (data?.resource) {
 *     updateHeader({
 *       metadata: [
 *         { label: 'Status', value: { type: 'text', content: data.resource.status } },
 *       ],
 *     })
 *   }
 * }, [data?.resource?.status, updateHeader])
 *
 * // Clear override to go back to route meta
 * const resetHeader = () => updateHeader(null)
 * ```
 */
export const useResourceHeader = () => {
  const context = useResponsivePage()

  return {
    headerProps: context.headerOverride,
    updateHeader: context.setHeaderOverride,
    updateMetadataProperty: context.updateMetadataProperty,
  }
}

/**
 * Hook for modules to add actions to the PageHeader (e.g. cart, custom buttons).
 * Actions are cleared when navigating away from the module.
 *
 * @example
 * ```tsx
 * const { setModuleHeaderActions } = useModuleHeaderActions()
 * useEffect(() => {
 *   setModuleHeaderActions([
 *     { label: 'Cart (3)', icon: Marketplace, href: `${pathname}#cart` },
 *   ])
 *   return () => setModuleHeaderActions(null)
 * }, [pathname, cartCount])
 * ```
 */
export const useModuleHeaderActions = () => {
  const context = useResponsivePage()
  return {
    setModuleHeaderActions: context.setModuleHeaderActions,
  }
}

export function useRouteMeta(routeMeta: RouteMetaWithPathname | null): ResolvedMeta | null {
  const [resolvedMeta, setResolvedMeta] = useState<ResolvedMeta | null>(null)
  const { context } = useMetaContext()

  useLayoutEffect(() => {
    if (!routeMeta) return
    const { meta, pathname } = routeMeta
    const pendingMeta = meta(context)

    if (pendingMeta instanceof Observable) {
      const subscription = pendingMeta.subscribe((metaData) => {
        setResolvedMeta({
          pathname: routeMeta.pathname,
          data: metaData,
        })
      })

      return () => subscription.unsubscribe()
    }

    if (pendingMeta instanceof Promise) {
      pendingMeta.then((metaData) => {
        setResolvedMeta({
          pathname: routeMeta.pathname,
          data: metaData,
        })
      })
      return
    }

    setResolvedMeta({
      pathname,
      data: pendingMeta,
    })
  }, [routeMeta, context])

  return resolvedMeta
}
